/**
 * 未登录（401）统一处理策略
 *
 * 目标：
 * 1. 控制台页面可以匿名浏览 —— 页面加载期间的只读请求（GET/HEAD/OPTIONS）遇到 401 时
 *    静默处理，不跳转登录页、不弹错误提示。
 * 2. 只有真正触发写操作（POST/PUT/PATCH/DELETE，例如点击 AI 生成按钮）时才提示用户登录，
 *    并且只做轻量提示，不做整页跳转。
 */

import { message } from 'antd';

const AUTH_STORAGE_KEY = 'simon-cat-auth';
const UNAUTHORIZED_EVENT = 'simon-cat:unauthorized';

type PromptHandler = (text: string) => void;

/** 由 App 层通过 App.useApp() 注入，使提示继承当前主题 */
let promptHandler: PromptHandler | null = null;

export function registerPromptHandler(handler: PromptHandler | null): void {
  promptHandler = handler;
}

/** 会改变服务端数据的请求方法 */
const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];

/** 提示节流，避免一次操作触发多个请求时重复弹提示 */
let lastPromptAt = 0;
const PROMPT_THROTTLE_MS = 3000;

/** 判断响应是否为未登录 */
export function isUnauthorized(error: any): boolean {
  return error?.response?.status === 401;
}

/** 清除本地登录态 */
export function clearAuthStorage(): void {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // localStorage 不可用时忽略
  }
}

/**
 * 提示用户登录（带节流）
 */
export function promptLogin(text = '请先登录后再操作'): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - lastPromptAt < PROMPT_THROTTLE_MS) return;
  lastPromptAt = now;
  if (promptHandler) {
    promptHandler(text);
    return;
  }
  message.warning(text);
}

/**
 * 处理 401 响应
 * - 清理本地登录态并广播事件（用于同步 zustand store）
 * - 写请求：提示用户登录
 * - 读请求：静默，保证控制台可以匿名访问
 */
export function handleUnauthorized(error: any): void {
  clearAuthStorage();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }

  const method = String(error?.config?.method || 'get').toLowerCase();
  if (WRITE_METHODS.includes(method)) {
    promptLogin();
  }
}

/**
 * 处理业务码形式的未登录（HTTP 200 + { code: 401 }）
 */
export function handleBusinessUnauthorized(config?: { method?: string }): void {
  clearAuthStorage();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }

  const method = String(config?.method || 'get').toLowerCase();
  if (WRITE_METHODS.includes(method)) {
    promptLogin();
  }
}
